/** Create-project dialog: validates with the shared schema, POSTs /api/projects, surfaces 409 slug conflicts */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { X } from 'lucide-react'
import { createProjectSchema, DEFAULT_WIKI_TEMPLATE, type CreateProjectInput, type WikiTemplateId } from '@agentwiki/shared'
import { apiErrorCode, useCreateProject } from '../../hooks/use-projects'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { projectPath } from './nav-config'
import { WikiTemplatePicker } from '../setup/setup-pickers'

const inputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-base text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent md:text-sm'

/** Mounted once at the app root; opened via the store so the sidebar and ⌘K share it */
export function CreateProjectDialogHost() {
  const open = useAppStore((s) => s.createProjectOpen)
  const setOpen = useAppStore((s) => s.setCreateProjectOpen)
  if (!open) return null
  return <CreateProjectDialog onClose={() => setOpen(false)} />
}

function CreateProjectDialog({ onClose }: { onClose: () => void }) {
  const t = useT()
  const navigate = useNavigate()
  const createProject = useCreateProject()
  const titleId = useId()
  const nameRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [repoUrl, setRepoUrl] = useState('')
  const [wikiTemplate, setWikiTemplate] = useState<WikiTemplateId>(DEFAULT_WIKI_TEMPLATE)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    nameRef.current?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Empty optional fields are omitted so the API derives the slug from the name
    const raw: CreateProjectInput = { name, wikiTemplate }
    if (slug.trim()) raw.slug = slug
    if (description.trim()) raw.description = description
    if (repoUrl.trim()) raw.repoUrl = repoUrl

    const parsed = createProjectSchema.safeParse(raw)
    if (!parsed.success) {
      const field = parsed.error.issues[0]?.path[0]
      setError(
        field === 'name'
          ? t('project.create.nameRequired')
          : field === 'slug'
            ? t('project.create.slugInvalid')
            : t('project.create.failed', { message: parsed.error.issues[0]?.message ?? '' }),
      )
      return
    }

    try {
      const project = await createProject.mutateAsync(parsed.data)
      toast.success(t('project.create.created', { name: project.name }))
      onClose()
      navigate(projectPath(project.slug))
    } catch (err) {
      if (apiErrorCode(err) === 'PROJECT_SLUG_TAKEN') {
        setError(parsed.data.slug ? t('project.create.slugTaken', { slug: parsed.data.slug }) : t('project.create.nameTaken'))
      } else {
        setError(t('project.create.failed', { message: err instanceof Error ? err.message : String(err) }))
      }
    }
  }

  const pending = createProject.isPending

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto px-4 py-[10vh]" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md rounded-xl border border-border bg-surface-raised p-5 text-fg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id={titleId} className="font-display text-lg font-semibold">
            {t('project.create.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="cursor-pointer rounded-md p-1.5 text-fg-subtle hover:bg-surface-hover hover:text-fg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field label={t('project.create.name')}>
            {(id) => (
              <input
                id={id}
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('project.create.namePlaceholder')}
                maxLength={120}
                required
                className={inputClass}
              />
            )}
          </Field>
          <Field label={t('project.create.slug')} hint={t('project.create.slugHint')}>
            {(id, hintId) => (
              <input
                id={id}
                aria-describedby={hintId}
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                maxLength={64}
                autoCapitalize="off"
                spellCheck={false}
                className={inputClass}
              />
            )}
          </Field>
          <Field label={t('project.create.description')}>
            {(id) => (
              <textarea
                id={id}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
                rows={3}
                className={`${inputClass} resize-y`}
              />
            )}
          </Field>
          <Field label={t('project.create.repoUrl')}>
            {(id) => (
              <input
                id={id}
                type="url"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                maxLength={500}
                placeholder="https://github.com/org/repo"
                className={inputClass}
              />
            )}
          </Field>
          <WikiTemplatePicker value={wikiTemplate} onChange={setWikiTemplate} />

          {error && (
            <p role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded-md border border-border px-3 py-2 text-sm font-medium text-fg-muted hover:bg-surface-hover hover:text-fg"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={pending}
              className="cursor-pointer rounded-md bg-accent px-3 py-2 text-sm font-semibold text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {pending ? t('project.create.submitting') : t('project.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: (id: string, hintId?: string) => React.ReactNode }) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-fg-muted">
        {label}
      </label>
      {children(id, hintId)}
      {hint && (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      )}
    </div>
  )
}
